import type { IMFile, IMForwarding, IMQuote } from '@wui/im'

import { getFileTypeByName, IMAGE_TYPE } from '@/shared/utils/files'
import { decodeHtmlEntities } from '@/shared/utils/htmlEntitiesDecode'
import { sanitizeToText } from '@/shared/utils/sanitize'

const formatFileSizeShort = (bytes?: number): string => {
  if (bytes == null || Number.isNaN(bytes)) {
    return ''
  }
  if (bytes < 1024) {
    return `${bytes} B`
  }
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(bytes < 10 * 1024 ? 2 : 1)} KB`
  }
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export type NotificationTranslateFn = (key: string, params?: Record<string, string | number>) => string

export type MessageNotificationBodySource = {
  messageText?: string
  files?: IMFile[]
  forwarding?: IMForwarding
  quote?: IMQuote
}

const truncate = (value: string, max: number) =>
  value.length <= max ? value : `${value.slice(0, max - 1)}…`

const isImageFile = (file: IMFile) => getFileTypeByName(file.filename) === IMAGE_TYPE

const findFirstImage = (files?: IMFile[]) => files?.find(isImageFile)

const describeFiles = (files: IMFile[] | undefined, t: NotificationTranslateFn): string | undefined => {
  if (!files?.length) {
    return
  }
  const first = files[0]
  if (!first) {
    return
  }
  if (files.length > 1) {
    const firstImage = findFirstImage(files)
    if (firstImage) {
      return t('common.notificationAndMoreAttachments', { count: files.length - 1 })
    }
    return t('mainAside.files', { count: files.length })
  }
  const size = formatFileSizeShort(first.filesize)
  return isImageFile(first)
    ? t('mainAside.image', { name: first.filename, size })
    : t('mainAside.fileDraft', { name: first.filename, size })
}

const normalizedText = (raw?: string) =>
  decodeHtmlEntities(sanitizeToText((raw ?? '').trim()))

/**
 * Текст и опционально относительный путь к превью-картинке для Web Notification
 * (цитата, пересылка, вложения, пустой messageText).
 */
export const buildMessageNotificationBody = (
  src: MessageNotificationBodySource,
  t: NotificationTranslateFn,
): { body: string, imageRelativePath: string | undefined } => {
  const lines: string[] = []

  if (src.quote?.quotedEventId || src.quote?.quotedText) {
    const quoted = normalizedText(src.quote.quotedText)
    if (quoted) {
      lines.push(`${t('common.quotedMessage')}: ${truncate(quoted, 180)}`)
    } else if (src.quote.quotedEventId) {
      lines.push(t('common.quotedMessage'))
    }
  }

  if (src.forwarding?.forwardingEventId) {
    const f = src.forwarding
    const header = f.forwardingAuthorName
      ? `${t('chat.forwardMessageIsReady')}: ${f.forwardingAuthorName}`
      : t('chat.forwardMessageIsReady')
    lines.push(header)
    const forwarded = normalizedText(f.forwardingText)
    if (forwarded) {
      lines.push(truncate(forwarded, 220))
    }
    const forwardedFiles = describeFiles(f.forwardingFiles, t)
    if (forwardedFiles) {
      lines.push(forwardedFiles)
    }
  }

  const ownFiles = describeFiles(src.files, t)
  if (ownFiles) {
    lines.push(ownFiles)
  }

  const main = normalizedText(src.messageText)
  if (main) {
    lines.push(truncate(main, 320))
  }

  let imageRelativePath: string | undefined
  const pickFirstImagePath = (files?: IMFile[]) => {
    const file = findFirstImage(files)
    if (file) {
      imageRelativePath = `/files/${file.filecookie}`
    }
  }
  pickFirstImagePath(src.files)
  if (!imageRelativePath) {
    pickFirstImagePath(src.forwarding?.forwardingFiles)
  }

  const body = lines.filter(Boolean).join('\n').trim() || t('common.notificationNoTextContent')

  return { body, imageRelativePath }
}
